import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./Film.css";
import Carousel from "../carousels/Carousel";
import { useAuth } from "../../auth/AuthContext";
import { apiRequest } from "../../api/client";
import "./Watchlist.css";
import StarRating from "../Rating/StarRating";
import "./FilmDetailLayout.css";
import CastRow from "./CastRow";

// Images for film posters
const getPosterURL = (posterpath: string) => {
  return `https://www.themoviedb.org/t/p/w220_and_h330_face${posterpath}`;
};

// Icons for WatchProviders
const getIconURL = (iconpath: string) => {
  return `https://image.tmdb.org/t/p/original/${iconpath}`;
};

const convertRunTime = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${mins}m`;
};

const Film = () => {
  // film_id from the url
  const { film_id } = useParams();
  const [filmData, setFilmData] = useState<any>();
  const [watchProviders, setWatchProviders] = useState<any>();
  const [similarFilms, setSimilarFilms] = useState<any>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [myRating, setMyRating] = useState<number | null>(null);
  const [hasSeen, setHasSeen] = useState(false);

  // GET filmData
  useEffect(() => {
    let cancelled = false;
    apiRequest<any>(`/movies/${film_id}`)
      .then(async (data) => {
        if (!cancelled) {
          setFilmData(data);
          setWatchProviders(data.providers);
          setSimilarFilms(data.similar || []);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [film_id]);

  useEffect(() => {
    if (!user || !film_id) { setIsSaved(false); return; }
    apiRequest<any>('/watchlist')
      .then(data => setIsSaved((data.items || []).some((item: any) => item.movieId === film_id)))
      .catch(() => undefined);
  }, [user, film_id]);

  useEffect(() => {
    if (!user || !film_id) { setMyRating(null); setHasSeen(false); return; }
    apiRequest<any>('/history')
      .then(data => { const item = (data.items || []).find((entry: any) => entry.movieId === film_id); setHasSeen(Boolean(item)); setMyRating(item?.rating || null); })
      .catch(() => undefined);
  }, [user, film_id]);

  const toggleWatchlist = async () => {
    if (!filmData || !film_id) return;
    setSaveMessage('');
    try {
      if (isSaved) await apiRequest(`/watchlist/${film_id}`, { method: 'DELETE' });
      else await apiRequest(`/watchlist/${film_id}`, { method: 'PUT', body: JSON.stringify({ title: filmData.title, posterPath: filmData.poster_path }) });
      setIsSaved(!isSaved);
    } catch { setSaveMessage('Could not update your watchlist. Please try again.'); }
  };

  const rateFilm = async (rating: number) => {
    if (!filmData || !film_id) return;
    setSaveMessage('');
    try {
      await apiRequest(`/history/${film_id}`, { method: 'PUT', body: JSON.stringify({ title: filmData.title, posterPath: filmData.poster_path, genreIds: filmData.genres.map((genre: any) => genre.id), rating, source: 'film' }) });
      setMyRating(rating);
      setHasSeen(true);
      setSaveMessage('Saved to your film history.');
    } catch { setSaveMessage('Could not save your rating. Please try again.'); }
  };

  const markSeen = async () => {
    if (!filmData || !film_id) return;
    setSaveMessage('');
    try {
      await apiRequest(`/history/${film_id}`, { method: 'PUT', body: JSON.stringify({ title: filmData.title, posterPath: filmData.poster_path, genreIds: filmData.genres.map((genre: any) => genre.id), source: 'film' }) });
      setHasSeen(true);
      setSaveMessage('Marked as seen. Add a rating whenever you like.');
    } catch { setSaveMessage('Could not update your film history. Please try again.'); }
  };

  return (
    <>
      {filmData && (
        <div className="film__container">
          <button onClick={() => navigate(-1)} className="back__button">
            <span aria-hidden="true">←</span> Back to films
          </button>
          <div className="film">
            <img
              className="film__image"
              src={getPosterURL(filmData.poster_path)}
              alt={`${filmData.title} poster`}
            />
            <div className="film__info">
              <p className="film__eyebrow">Now discovering</p>
              <h1 className="film__title">{filmData.title}</h1>
              <div className="film__header--genre">
                {filmData.genres.map((genre: any) => genre.name).join(", ")}
              </div>
              <div className="film__facts">
                <span className="film__facts-rating">★ {(filmData.vote_average / 2).toFixed(1)} / 5</span>
                {filmData.release_date && <span>{filmData.release_date.slice(0, 4)}</span>}
                {filmData.runtime > 0 && <span>{convertRunTime(filmData.runtime)}</span>}
                {filmData.imdb_id && <a href={`https://www.imdb.com/title/${filmData.imdb_id}`} target="_blank" rel="noreferrer">IMDb ↗</a>}
              </div>

              {user ? (
                <><button className={`watchlist__button ${isSaved ? 'watchlist__button--saved' : ''}`} onClick={toggleWatchlist}>
                  <span aria-hidden="true">{isSaved ? '✓' : '+'}</span> {isSaved ? 'Saved to watchlist' : 'Add to watchlist'}
                </button><div className="seen-controls"><button className={`seen-button ${hasSeen ? 'seen-button--active' : ''}`} onClick={markSeen} disabled={hasSeen}>{hasSeen ? '✓ Seen' : '✓ Mark as seen'}</button><div className="my-rating"><span>{myRating ? 'Your rating' : 'Optional — add a rating'}</span><StarRating value={myRating} onChange={rateFilm} label={`Your rating for ${filmData.title}`} /></div></div></>
              ) : (
                <button className="watchlist__button" onClick={() => navigate('/account')}>Sign in to save this film</button>
              )}
              {saveMessage && <p className="watchlist__message" role="alert">{saveMessage}</p>}

              {watchProviders ? (
                <div className="film__providers">
                  {watchProviders.flatrate && (
                    <div className="stream__providers">
                      <div className="film__minor-info">
                        <span className="helper__blue">Stream: </span>
                      </div>
                      {watchProviders.flatrate.map((provider: any) => (
                        <img
                          key={provider.provider_id}
                          src={getIconURL(provider.logo_path)}
                          alt={provider.provider_name + " logo"}
                        />
                      ))}
                    </div>
                  )}

                  {watchProviders.buy && (
                    <div className="buy__providers">
                      <div className="film__minor-info">
                        <span className="helper__blue">Buy: </span>
                      </div>
                      {watchProviders.buy.map((provider: any) => (
                        <img
                          key={provider.provider_id}
                          src={getIconURL(provider.logo_path)}
                          alt={provider.provider_name + " logo"}
                        />
                      ))}
                    </div>
                  )}

                  {watchProviders.rent && (
                    <div className="rent__providers">
                      <div className="film__minor-info">
                        <span className="helper__blue">Rent: </span>
                      </div>
                      {watchProviders.rent.map((provider: any) => (
                        <img
                          key={provider.provider_id}
                          src={getIconURL(provider.logo_path)}
                          alt={provider.provider_name + " logo"}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                "Not currently available to stream."
              )}
              <div className="film__description">{filmData.overview}</div>
            </div>
          </div>
          <CastRow cast={filmData.cast || []} directors={filmData.directors || []} signedIn={Boolean(user)} />
          {similarFilms && (
            <Carousel
              films={similarFilms}
              title={`Films similar to`}
              highlight={filmData.title}
            />
          )}

          <div className="data__source">
            Watch provider data provided by{" "}
            <a href="https://www.justwatch.com/">JustWatch</a>.
          </div>
        </div>
      )}
    </>
  );
};

export default Film;
